import { useEffect, useState } from "react"
import Box from "@mui/material/Box"
import IconButton from "@mui/material/IconButton"
import Typography from "@mui/material/Typography"
import Tooltip from "@mui/material/Tooltip"
import Avatar from "@mui/material/Avatar"
import Popover from "@mui/material/Popover"
import ReplyIcon from "@mui/icons-material/Reply"
import AddReactionOutlinedIcon from "@mui/icons-material/AddReactionOutlined"
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined"
import ForwardOutlinedIcon from "@mui/icons-material/ForwardOutlined"
import DoneAllIcon from "@mui/icons-material/DoneAll"
import AlarmAddOutlinedIcon from "@mui/icons-material/AlarmAddOutlined"
import { MONO, CHAT_COLOR, AGENT_COLORS, EFFECT_SHADOW, QUICK_REACTIONS } from "../constants"
import { renderWithCode } from "../utils/chatText"
import { VoicePlayer } from "./VoicePlayer"
import { ReactionBar } from "./ReactionBar"
import { FileChip } from "./FileChip"
import { LinkPreview } from "./LinkPreview"

export function MsgBubble({
  msg, me, profiles, users, onVaultLink, showEffect, effect, onReply, onProfileClick,
  onImageClick, onJumpToMessage, highlighted, reactions, threadCount, seenBy, onReact, onOpenThread, onForward, onRemind,
}) {
  const [show, setShow] = useState(false)
  const [hovered, setHovered] = useState(false)
  const [reactAnchor, setReactAnchor] = useState(null)
  useEffect(() => { const id = requestAnimationFrame(() => setShow(true)); return () => cancelAnimationFrame(id) }, [])

  const sender = msg.username ?? msg.from ?? "?"
  const isMine = sender === me
  const isAgent = sender === "jarvis" || sender === "friday"
  const ts = new Date((msg.timestamp ?? 0) * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  const profile = profiles?.[sender]
  const agentColor = AGENT_COLORS[sender] ?? null
  const senderColor = profile?.name_color ?? agentColor ?? CHAT_COLOR
  const displayName = profile?.display_name ?? (isAgent ? sender.toUpperCase() : sender)
  const avatarSrc = profile?.avatar_b64 || undefined
  const activeShadow = showEffect && isMine ? (EFFECT_SHADOW[effect] ?? null) : null
  const knownUsers = new Set(users.map(u => u.username.toLowerCase()))
  const othersWhoSaw = isMine ? (seenBy ?? []).filter(u => u !== sender) : []

  return (
    <Box
      id={"msg-" + msg.id}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: "flex", flexDirection: "column", alignItems: isMine ? "flex-end" : "flex-start",
        mb: 0.75, opacity: show ? 1 : 0, transform: show ? "translateY(0)" : "translateY(5px)",
        transition: "opacity 0.18s ease-out, transform 0.18s ease-out, background-color 0.3s ease-out",
        position: "relative", borderRadius: 1,
        backgroundColor: highlighted ? "rgba(245,158,11,0.08)" : "transparent",
      }}
    >
      {!isMine && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.3, mx: 0.5 }}>
          <Box onClick={(e) => onProfileClick?.(e.currentTarget, sender)}
            sx={{ cursor: "pointer", "&:hover": { opacity: 0.75 } }}>
            {avatarSrc
              ? <Avatar src={avatarSrc} sx={{ width: 28, height: 28, flexShrink: 0 }} />
              : <Avatar sx={{ width: 28, height: 28, flexShrink: 0, bgcolor: `color-mix(in srgb, ${senderColor} 13.3%, transparent)`,
                  border: "1px solid " + `color-mix(in srgb, ${senderColor} 26.7%, transparent)`, color: senderColor, fontSize: 11, fontFamily: MONO }}>
                  {displayName[0]?.toUpperCase()}
                </Avatar>}
          </Box>
          <Typography onClick={(e) => onProfileClick?.(e.currentTarget, sender)}
            sx={{ color: senderColor, fontFamily: MONO, fontSize: "0.63rem", opacity: 0.9,
              cursor: "pointer", "&:hover": { opacity: 0.65 } }}>
            {displayName}
          </Typography>
        </Box>
      )}

      {msg.reply_to && (
        <Box
          onClick={() => onJumpToMessage?.(msg.reply_to.id)}
          sx={{
            maxWidth: "82%", mb: 0.3, px: 1, py: 0.4, borderRadius: "6px",
            backgroundColor: "rgba(var(--app-fg-rgb),0.04)",
            borderLeft: "2px solid " + (isMine ? CHAT_COLOR : senderColor),
            opacity: 0.7,
            cursor: onJumpToMessage ? "pointer" : "default",
            "&:hover": onJumpToMessage ? { opacity: 1, backgroundColor: "rgba(var(--app-fg-rgb),0.08)" } : {},
            transition: "opacity 0.12s, background-color 0.12s",
          }}>
          <Typography sx={{ fontFamily: MONO, fontSize: "0.58rem",
            color: isMine ? CHAT_COLOR : senderColor, mb: 0.1 }}>
            {profiles?.[msg.reply_to.username]?.display_name ?? msg.reply_to.username}
          </Typography>
          <Typography sx={{ fontFamily: MONO, fontSize: "0.62rem", color: "rgba(var(--app-fg-rgb),0.5)",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}>
            {msg.reply_to.text}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "flex-end",
        flexDirection: isMine ? "row-reverse" : "row", width: "100%", position: "relative" }}>
        <Box sx={{
          maxWidth: "84%", px: 1.25, py: 0.6,
          borderRadius: isMine ? "10px 10px 2px 10px" : "10px 10px 10px 2px",
          backgroundColor: isMine ? "rgba(245,158,11,0.1)" : isAgent ? `color-mix(in srgb, ${senderColor} 6.7%, transparent)` : "rgba(var(--app-fg-rgb),0.06)",
          border: "1px solid " + (isMine ? "rgba(245,158,11,0.22)" : isAgent ? `color-mix(in srgb, ${senderColor} 20%, transparent)` : "rgba(var(--app-fg-rgb),0.07)"),
          boxShadow: activeShadow ?? "none",
          transition: "box-shadow 0.4s ease-out",
        }}>
          {msg.image && (
            <Box component="img" src={msg.image} alt=""
              onClick={() => onImageClick?.(msg.image)}
              sx={{ maxWidth: "100%", maxHeight: 200, borderRadius: 1, display: "block",
                mb: msg.text ? 0.5 : 0, cursor: "zoom-in" }} />
          )}
          {msg.voice && (
            <Box sx={{ mb: msg.text ? 0.5 : 0 }}>
              <VoicePlayer voice={msg.voice} mime={msg.voice_mime} />
            </Box>
          )}
          {msg.file && msg.file_name && (
            <Box sx={{ mb: msg.text ? 0.5 : 0 }}>
              <FileChip file={msg.file} fileName={msg.file_name} />
            </Box>
          )}
          {msg.text && (
            <Typography sx={{ fontFamily: MONO, fontSize: "0.72rem", color: "rgba(var(--app-fg-rgb),0.88)",
              wordBreak: "break-word", lineHeight: 1.55 }}>
              {renderWithCode(msg.text, onVaultLink, knownUsers)}
            </Typography>
          )}
          {msg.text && !String(msg.id).startsWith("stream-") && <LinkPreview text={msg.text} />}
        </Box>

        {(hovered || reactAnchor) && (onReply || onReact || onOpenThread || onForward || onRemind) && (
          <Box sx={{
            position: "absolute", zIndex: 1, top: -9,
            ...(isMine ? { right: 4 } : { left: 4 }),
            display: "flex", flexDirection: isMine ? "row-reverse" : "row",
            px: 0.25, py: "1px", borderRadius: "6px",
            backgroundColor: "rgba(10,10,20,0.94)",
            border: "1px solid rgba(var(--app-fg-rgb),0.08)",
            boxShadow: "0 2px 8px rgba(var(--app-shadow-rgb),0.35)",
          }}>
            {onReact && (
              <Tooltip title="React" placement={isMine ? "left" : "right"} arrow>
                <IconButton size="small" onClick={(e) => setReactAnchor(e.currentTarget)}
                  sx={{ p: "2px", color: "rgba(var(--app-fg-rgb),0.25)", "&:hover": { color: CHAT_COLOR } }}>
                  <AddReactionOutlinedIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>
            )}
            {onOpenThread && (
              <Tooltip title="Reply in thread" placement={isMine ? "left" : "right"} arrow>
                <IconButton size="small" onClick={() => onOpenThread(msg)}
                  sx={{ p: "2px", color: "rgba(var(--app-fg-rgb),0.25)", "&:hover": { color: CHAT_COLOR } }}>
                  <ForumOutlinedIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>
            )}
            {onForward && (
              <Tooltip title="Forward" placement={isMine ? "left" : "right"} arrow>
                <IconButton size="small" onClick={(e) => onForward(e.currentTarget, msg)}
                  sx={{ p: "2px", color: "rgba(var(--app-fg-rgb),0.25)", "&:hover": { color: CHAT_COLOR } }}>
                  <ForwardOutlinedIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>
            )}
            {onRemind && msg.text && (
              <Tooltip title="Remind me" placement={isMine ? "left" : "right"} arrow>
                <IconButton size="small" onClick={() => onRemind(msg)}
                  sx={{ p: "2px", color: "rgba(var(--app-fg-rgb),0.25)", "&:hover": { color: "var(--feature-reminder)" } }}>
                  <AlarmAddOutlinedIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>
            )}
            {onReply && (
              <Tooltip title="Reply" placement={isMine ? "left" : "right"} arrow>
                <IconButton size="small" onClick={() => onReply(msg)}
                  sx={{ p: "2px", color: "rgba(var(--app-fg-rgb),0.25)", "&:hover": { color: CHAT_COLOR } }}>
                  <ReplyIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>

      {onReact && (
        <Popover data-chat-overlay="true" open={Boolean(reactAnchor)} anchorEl={reactAnchor} onClose={() => setReactAnchor(null)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          transformOrigin={{ vertical: "bottom", horizontal: "center" }}
          PaperProps={{ "data-chat-overlay": "true", sx: { backgroundColor: "var(--app-paper)",
            border: "1px solid rgba(245,158,11,0.22)", borderRadius: "8px", p: 0.5,
            display: "flex", gap: 0.25 } }}>
          {QUICK_REACTIONS.map(e => (
            <Box key={e} onClick={() => { onReact(msg.id, e); setReactAnchor(null) }}
              sx={{ fontSize: "1rem", cursor: "pointer", p: "3px 4px", borderRadius: "4px",
                "&:hover": { backgroundColor: "rgba(var(--app-fg-rgb),0.08)" } }}>
              {e}
            </Box>
          ))}
        </Popover>
      )}

      {onReact && <ReactionBar reactions={reactions} me={me} onToggle={(emoji) => onReact(msg.id, emoji)} />}

      {onOpenThread && threadCount > 0 && (
        <Typography onClick={() => onOpenThread(msg)}
          sx={{ fontFamily: MONO, fontSize: "0.6rem", color: CHAT_COLOR, opacity: 0.8, mt: 0.3, mx: 0.5,
            cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>
          {threadCount} {threadCount === 1 ? "reply" : "replies"}
        </Typography>
      )}

      {onOpenThread && msg.thread_id && (
        <Typography onClick={() => onOpenThread(msg)}
          sx={{ fontFamily: MONO, fontSize: "0.6rem", color: CHAT_COLOR, opacity: 0.8, mt: 0.3, mx: 0.5,
            cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>
          ↳ Reply in thread · view conversation
        </Typography>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, mt: 0.2, mx: 0.5 }}>
        <Typography sx={{ color: "rgba(var(--app-fg-rgb),0.22)", fontFamily: MONO, fontSize: "0.57rem" }}>
          {ts}
        </Typography>
        {isMine && othersWhoSaw.length > 0 && (
          <Tooltip title={"Seen by " + othersWhoSaw.join(", ")} arrow>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.15, color: "var(--app-success)" }}>
              <DoneAllIcon sx={{ fontSize: 10 }} />
            </Box>
          </Tooltip>
        )}
      </Box>
    </Box>
  )
}

export default MsgBubble
